import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { CloudUpload, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDecks } from '../hooks/useDecks';
import { DeckGrid } from '../components/deck/DeckGrid';
import { DeckList } from '../components/deck/DeckList';
import { DeckFilters } from '../components/deck/DeckFilters';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { ConfirmDialog } from '../components/common/ConfirmDialog';

export const DecksListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState<string | null>(null);

  const { decks, loading, deleting, error, deleteDeck, refresh } = useDecks({
    status: statusFilter === 'all' ? undefined : statusFilter,
    searchQuery: searchQuery || undefined,
  });

  const handleDeleteClick = (deckId: string) => {
    setDeckToDelete(deckId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deckToDelete) {
      // Immediately close dialog and show loading state
      setDeleteDialogOpen(false);
      const deckId = deckToDelete;
      setDeckToDelete(null);
      
      // Then perform the delete operation
      try {
        await deleteDeck(deckId);
      } catch (err) {
        console.error('Failed to delete deck:', err);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeckToDelete(null);
  };

  // Combined loading state for both fetching and deleting
  const isLoading = loading || deleting;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            My Presentations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and review your analyzed presentations
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => navigate('/upload')}
          >
            Upload New
          </Button>
        </Box>
      </Box>

      <DeckFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {isLoading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} />
        </Box>
      )}

      {error && <ErrorDisplay error={error} onRetry={refresh} />}

      {!isLoading && !error && decks.length === 0 && (
        <EmptyState
          title="No presentations found"
          description="Upload your first presentation to get started"
          actionLabel="Upload Presentation"
          onAction={() => navigate('/upload')}
        />
      )}

      {!isLoading && !error && decks.length > 0 && (
        viewMode === 'grid' ? (
          <DeckGrid decks={decks} onDelete={handleDeleteClick} onReanalyzeSuccess={refresh} />
        ) : (
          <DeckList decks={decks} onDelete={handleDeleteClick} onReanalyzeSuccess={refresh} />
        )
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Presentation"
        message="Are you sure you want to delete this presentation? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        severity="error"
      />
    </Container>
  );
};

